import { twMerge } from 'tailwind-merge';

export type HtmlWarning = {
  code: string;
  message: string;
};

function stripCodeFences(input: string): string {
  return String(input || '')
    .replace(/^\s*```(?:html)?\s*\n?/i, '')
    .replace(/\n?```\s*$/i, '')
    .trim();
}

function stripOuterDocumentTags(input: string): string {
  // If the model returns a full doc, keep only body innerHTML.
  const html = String(input || '');
  if (!/[<]html\b/i.test(html) && !/[<]body\b/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body?.innerHTML ?? html;
  } catch {
    return html;
  }
}

export function normalizeGeneratedHtml(rawHtml: string): string {
  const trimmed = stripOuterDocumentTags(stripCodeFences(rawHtml)).replace(/\bclassName(\s*=\s*)/g, 'class$1');

  try {
    const doc = new DOMParser().parseFromString(trimmed, 'text/html');

    // Strip external/injected resources.
    doc.querySelectorAll('script, link, style, meta, title').forEach((el) => el.remove());

    // Remove common "splash" overlays (they often block the editor view).
    doc
      .querySelectorAll<HTMLElement>(
        '#splash,[id^="splash" i],[id*="splash" i],[data-section="splash"],[data-screen="splash"]'
      )
      .forEach((el) => el.remove());

    // If an element looks like a full-screen welcome overlay, remove it.
    doc.querySelectorAll<HTMLElement>('body *').forEach((el) => {
      const id = (el.getAttribute('id') ?? '').toLowerCase();
      const cls = (el.getAttribute('class') ?? '').toLowerCase();
      const text = (el.textContent ?? '').toLowerCase();
      const looksFullscreen = cls.includes('fixed') && (cls.includes('inset-0') || cls.includes('inset'));
      const looksWelcome = text.includes('welcome') || text.includes('get started');
      if (!id && looksFullscreen && looksWelcome) {
        el.remove();
      }
    });

    // Strip inline event handlers and javascript: URLs.
    doc.querySelectorAll<HTMLElement>('*').forEach((el) => {
      for (const { name, value } of Array.from(el.attributes)) {
        if (/^on/i.test(name)) {
          el.removeAttribute(name);
          continue;
        }
        if (name.toLowerCase() === 'href' && /^\s*javascript:/i.test(value)) {
          el.removeAttribute(name);
        }
      }
    });

    // Normalize classes using Tailwind conflict resolution.
    doc.querySelectorAll<HTMLElement>('[class]').forEach((el) => {
      const cls = el.getAttribute('class');
      if (!cls) {
        el.removeAttribute('class');
        return;
      }
      // Reduce glassmorphism by default: remove backdrop-filter/blur and convert alpha colors (bg-*/50) to solid.
      const tokens = cls
        .split(/\s+/g)
        .filter(Boolean)
        .map((t) => {
          if (/^backdrop-/.test(t)) return '';
          if (/^backdrop-blur/.test(t)) return '';
          const m = t.match(/^(bg|text|border|ring)-(.+)\/\d+$/);
          if (m) return `${m[1]}-${m[2]}`;
          return t;
        })
        .filter(Boolean)
        .join(' ');

      const merged = twMerge(tokens);
      const next = merged.trim().replace(/\s+/g, ' ');
      if (!next) el.removeAttribute('class');
      else el.setAttribute('class', next);
    });

    return doc.body?.innerHTML ?? trimmed;
  } catch {
    // Fallback: at least strip className and codefences.
    return trimmed;
  }
}

function hasAccessibleName(el: Element): boolean {
  const ariaLabel = (el.getAttribute('aria-label') ?? '').trim();
  if (ariaLabel) return true;
  const ariaLabelledBy = (el.getAttribute('aria-labelledby') ?? '').trim();
  if (ariaLabelledBy) return true;

  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (text) return true;

  // Common case: icon-only buttons with an <img alt="...">.
  const imgAlt = Array.from(el.querySelectorAll('img'))
    .map((img) => (img.getAttribute('alt') ?? '').trim())
    .find(Boolean);
  if (imgAlt) return true;

  return false;
}

export function getHtmlWarnings(html: string): HtmlWarning[] {
  const warnings: HtmlWarning[] = [];
  const normalized = normalizeGeneratedHtml(html);

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(normalized, 'text/html');
  } catch {
    return warnings;
  }

  const root = doc.body;
  if (!root) return warnings;

  const h1Count = root.querySelectorAll('h1').length;
  if (h1Count === 0) {
    warnings.push({ code: 'h1-missing', message: 'No <h1> found. Add exactly one primary heading.' });
  } else if (h1Count > 1) {
    warnings.push({ code: 'h1-multiple', message: `Multiple <h1> found (${h1Count}). Keep exactly one.` });
  }

  const imgs = Array.from(root.querySelectorAll('img'));
  const imgsMissingAlt = imgs.filter((img) => !img.hasAttribute('alt')).length;
  if (imgsMissingAlt > 0) {
    warnings.push({
      code: 'img-alt-missing',
      message: `${imgsMissingAlt} image(s) missing alt attribute. Add alt (or alt=\"\" for decorative).`,
    });
  }

  const buttons = Array.from(root.querySelectorAll('button'));
  const buttonsMissingName = buttons.filter((b) => !hasAccessibleName(b)).length;
  if (buttonsMissingName > 0) {
    warnings.push({
      code: 'button-name-missing',
      message: `${buttonsMissingName} button(s) missing an accessible name. Add text or aria-label.`,
    });
  }

  const links = Array.from(root.querySelectorAll('a[href]'));
  const linksMissingName = links.filter((a) => !hasAccessibleName(a)).length;
  if (linksMissingName > 0) {
    warnings.push({
      code: 'link-name-missing',
      message: `${linksMissingName} link(s) missing an accessible name. Add text or aria-label.`,
    });
  }

  const nestedInteractiveCount =
    root.querySelectorAll('button button, button a[href], a[href] button, a[href] a[href]').length;
  if (nestedInteractiveCount > 0) {
    warnings.push({
      code: 'nested-interactive',
      message: `Found ${nestedInteractiveCount} nested interactive element(s) (e.g. button inside link). Remove nesting.`,
    });
  }

  return warnings;
}
